<template>
  <v-card>
    <v-card-title>
      <v-text-field
        v-model="search"
        append-icon="mdi-search"
        :label="searchLabel"
        single-line
        hide-details
        class="d-none d-sm-block"
      ></v-text-field>
      <v-spacer></v-spacer>
      <v-btn
        class="ma-2"
        tile
        outlined
        color="primary"
        :loading="applyLoading"
        v-if="createEnable || updateEnable"
        @click="applyPermissions"
      >
        <v-icon left>mdi-refresh</v-icon>{{ $t("apply changes") }}
      </v-btn>
      <v-btn
        class="ma-2"
        tile
        outlined
        color="primary"
        v-if="createEnable"
        @click="addNewToken"
      >
        <v-icon left>mdi-plus</v-icon>{{ $t("add token") }}
      </v-btn>
    </v-card-title>
    <v-data-table
      :headers="headers"
      :items="tokens"
      :search="search"
      :loading="loading"
      :items-per-page="itemsPerPage"
      :footer-props="footerProps"
      @click:row="handleUserTableClick"
    >
      <template v-slot:[`item.roles`]="{ item }">
        <v-chip
          v-for="role in item.roles"
          :key="role.name"
          class="ma-2"
          label
          small
          >{{ role.name }}</v-chip
        >
      </template>

      <template v-slot:[`item.token`]="{ item }">
        <v-btn
          class="ma-2"
          text
          icon
          color="lighten-2"
          v-on:click.stop="copyToken(item.token)"
        >
          <v-icon>mdi-content-copy</v-icon>
        </v-btn>
      </template>

      <template v-slot:[`item.expirationDate`]="{ item }">
        <v-chip
          v-if="!item.expirationDate"
          class="ma-2"
          color="primary"
          text-color="white"
        >
          <v-avatar left> <v-icon>mdi-checkbox-marked-circle</v-icon> </v-avatar
          >{{ $t("No expiration") }}
        </v-chip>

        <v-chip
          v-if="item.expirationDate && item.expirationDate > timestamp"
          class="ma-2"
          color="primary"
          text-color="white"
        >
          <v-avatar left>
            <v-icon>mdi-checkbox-marked-circle</v-icon>
          </v-avatar>

          <dataFormatter
            v-bind:date="item.expirationDate"
            input="timestamp"
            output="date"
          ></dataFormatter>
        </v-chip>

        <v-chip
          v-if="item.expirationDate && item.expirationDate <= timestamp"
          class="ma-2"
          color="red"
          text-color="white"
        >
          <v-avatar left>
            <v-icon>mdi-close-circle</v-icon>
          </v-avatar>

          <dataFormatter
            v-bind:date="item.expirationDate"
            input="timestamp"
            output="date"
          ></dataFormatter>
        </v-chip>
      </template>

      <template v-slot:[`item.modificationDate`]="{ item }">
        <dataFormatter
          v-bind:date="item.modificationDate"
          input="datetime"
          output="date"
        ></dataFormatter>
      </template>

      <template v-slot:[`item.enabled`]="{ item }">
        <v-checkbox
          v-model="item.enabled"
          :disabled="!updateEnable"
          @change="enabled(item.enabled, item.id)"
          inset
        ></v-checkbox>
      </template>
    </v-data-table>

    <UpdateTokenModal ref="updateTokenModal"></UpdateTokenModal>
    <NewTokenModal ref="newTokenModal"></NewTokenModal>
  </v-card>
</template>

<script>
import i18n from "../../../i18n";
import axios from "axios";
import NewTokenModal from "./NewTokenModal";
import UpdateTokenModal from "./UpdateTokenModal";
import EventBus from "../../../event-bus";
import DataFormatter from "../../Helpers/DataFormatter";

export default {
  name: "TokensTable",
  components: {
    NewTokenModal,
    UpdateTokenModal,
    DataFormatter,
  },
  data: () => ({
    loading: true,
    applyLoading: false,
    search: "",
    searchLabel: i18n.t("search"),
    itemsPerPage: 15,
    headers: [
      {
        text: i18n.t("Description"),
        align: "start",
        filterable: true,
        value: "description",
      },
      { text: i18n.t("Roles"), value: "roles" },
      { text: i18n.t("Token"), value: "token" },
      { text: i18n.t("Expiration"), value: "expirationDate" },
      { text: i18n.t("Modification Date"), value: "modificationDate" },
      { text: i18n.t("Enabled"), value: "enabled" },
    ],
    tokens: [],
    timestamp: Math.floor(Date.now() / 1000),
    createEnable: false,
    updateEnable: false,
    footerProps: {
      "items-per-page-text": i18n.t("Rows per page"),
    },
  }),
  created() {
    EventBus.$on(
      "UPDATE_TABLES",
      function () {
        this.updateTable();
      }.bind(this)
    );
  },
  mounted() {
    // Permissions
    if (typeof this.$store.getters.resources.permissions !== "undefined") {
      let permissions = this.$store.getters.resources.permissions;

      if (this.checkNested(permissions, "tokens", "create")) {
        this.createEnable = true;
      }

      if (this.checkNested(permissions, "tokens", "update")) {
        this.updateEnable = true;
      }
    }

    this.init();
  },
  methods: {
    init() {
      this.updateTable();
    },
    sendError(error) {
      this.loading = false;

      if (error.response) {
        if (
          error.response.status == 500 &&
          error.response.data.status == "error"
        ) {
          EventBus.$emit("SHOW_ERROR", error.response.data.error);
        } else {
          EventBus.$emit("SHOW_ERROR", error.response.data);
        }
      } else {
        EventBus.$emit("SHOW_ERROR", error);
      }
    },
    copyToken(token) {
      navigator.clipboard.writeText(token);
      EventBus.$emit("SHOW_SNACKBAR", i18n.t("Copied to clipboard!"), 1000);
    },
    updateTable() {
      axios
        .get(
          "/api/v1/permissions/tokens?filter=description,roles,token,modificationDate,expirationDate,enabled"
        )
        .then((response) => {
          this.loading = false;
          this.tokens = [];

          if (response.data.status && response.data.status == "done") {
            this.tokens = response.data.list;
          } else {
            EventBus.$emit("SHOW_ERROR", response.data.error);
          }
        })
        .catch((error) => {
          this.sendError(error);
        });
    },
    addNewToken() {
      this.$refs.newTokenModal.show();
    },
    applyPermissions() {
      this.applyLoading = true;

      axios
        .post("/api/v1/permissions/apply")
        .then((response) => {
          this.applyLoading = false;

          if (
            !response.data ||
            !response.data.status ||
            !(response.data.status == "done")
          ) {
            EventBus.$emit("SHOW_ERROR", response);
          } else {
            EventBus.$emit(
              "SHOW_SNACKBAR",
              i18n.t("Permissions successfully applied!")
            );
          }
        })
        .catch((error) => {
          this.applyLoading = false;
          this.sendError(error);
        });
    },
    handleUserTableClick(token) {
      this.$refs.updateTokenModal.show(token.id);
    },
    enabled(enabled, id) {
      // Avoid UserConfigurationModel popup.
      event.stopPropagation();

      axios
        .put("/api/v1/permissions/token/" + id, { enabled: enabled })
        .then((response) => {
          if (
            !response.data ||
            !response.data.status ||
            !(response.data.status == "done")
          ) {
            EventBus.$emit("SHOW_ERROR", response);

            for (let i = 0; i < this.tokens.length; i++) {
              if (this.tokens[i].id == id) {
                this.tokens[i].enabled = true;
              }
            }
          }
        })
        .catch((error) => {
          this.sendError(error);

          for (let i = 0; i < this.tokens.length; i++) {
            if (this.tokens[i].id == id) {
              this.tokens[i].enabled = true;
            }
          }
        });
    },
    checkNested(obj, level, ...rest) {
      if (obj === undefined) return false;
      if (rest.length == 0 && {}.hasOwnProperty.call(obj, level)) return true;
      return this.checkNested(obj[level], ...rest);
    },
  },
};
</script>

<style scoped>
.v-input {
  text-transform: capitalize;
}
</style>